import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Truck,
  Zap,
  Wrench,
  Check,
} from "lucide-react";
const Hero3D = lazy(() => import("@/three/Hero3D"));
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scrollTo";
import api from "@/lib/api";

const categories = [
  {
    id: "celulares",
    title: "Celulares",
    copy: "Lacrados e semi-novos, revisados peça a peça.",
    img: "https://images.unsplash.com/photo-1790825366339-bb4961b10825?crop=entropy&cs=srgb&fm=jpg&q=85",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    id: "acessorios",
    title: "Acessórios",
    copy: "Fones, capas, carregadores e energia portátil.",
    img: "https://images.unsplash.com/photo-1634744536040-2e812cb5706f?crop=entropy&cs=srgb&fm=jpg&q=85",
    span: "md:col-span-1",
  },
  {
    id: "telas",
    title: "Telas",
    copy: "Painéis de reposição testados um a um.",
    img: "https://images.unsplash.com/photo-1634744536075-d22f42f88e54?crop=entropy&cs=srgb&fm=jpg&q=85",
    span: "md:col-span-1",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => {
        if (r.data.length > 0) return r.data;
        return api.get("/products").then((all) => all.data.slice(0, 4));
      })
      .then((data) => setFeatured(data.slice(0, 4)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div className="bg-ink">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative overflow-hidden"
        data-testid="home-hero"
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(640px 420px at 78% 18%, rgba(0,229,255,0.14), transparent 70%), linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
            backgroundSize: "auto, 90px 90px, 90px 90px",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-10 pt-28 md:pt-24 pb-14 grid lg:grid-cols-[1.02fr_0.98fr] gap-8 items-center min-h-screen">
          <motion.div style={{ y: yText, opacity: fade }}>
            <p className="flex items-center gap-3 text-cyan text-xs uppercase tracking-[0.22em] mb-7">
              <span className="h-px w-10 bg-cyan inline-block" />
              Loja de tecnologia em 3D
            </p>
            <h1 className="font-display font-semibold tracking-tight leading-[0.95] text-[clamp(3.1rem,8vw,6.4rem)]">
              <MaskedLines
                lines={[
                  "CELULARES,",
                  "ACESSÓRIOS",
                  <>
                    <span className="text-cyan">E TELAS.</span>
                  </>,
                ]}
                delay={0.15}
              />
            </h1>
            <p className="text-mute text-base md:text-lg leading-relaxed max-w-md mt-7">
              O próximo upgrade do seu celular mora aqui: aparelhos revisados,
              acessórios com curadoria e telas de reposição testadas — em uma
              loja que você gira, arrasta e explora.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <Link
                to="/loja"
                className="inline-flex h-12 items-center gap-2 rounded-md bg-cyan px-7 font-semibold text-ink transition-all duration-300 hover:bg-cyan-dark hover:-translate-y-0.5"
                data-testid="hero-cta-catalog"
              >
                Explorar catálogo
                <ArrowRight size={18} />
              </Link>
              <button
                onClick={() => scrollToSection("#destaques")}
                className="inline-flex h-12 items-center gap-2 rounded-md border border-white/20 px-7 text-white transition-all duration-300 hover:border-cyan hover:text-cyan hover:-translate-y-0.5"
                data-testid="hero-cta-featured"
              >
                Ver destaques
              </button>
            </div>
            <div className="flex flex-wrap gap-x-7 gap-y-3 mt-10 text-xs text-mute">
              {[
                { icon: Truck, label: "Frete grátis para todo o Brasil" },
                { icon: ShieldCheck, label: "Pagamento seguro via Mercado Pago" },
                { icon: Zap, label: "Estoque em tempo real" },
                { icon: Wrench, label: "Especialistas em telas" },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-2">
                  <Icon size={14} className="text-cyan" />
                  {label}
                </span>
              ))}
            </div>
          </motion.div>
          <div className="relative">
            <Suspense
              fallback={
                <div className="relative h-[440px] sm:h-[540px] lg:h-[640px]">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-[65%] h-[60%] rounded-full bg-cyan/10 blur-[110px]" />
                  </div>
                </div>
              }
            >
              <Hero3D />
            </Suspense>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Celulares",
          "Acessórios",
          "Telas de reposição",
          "Fones",
          "Capas",
          "Carregadores",
        ]}
      />

      {/* BENTO CATEGORIAS */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20 md:py-28" data-testid="home-categories">
        <FadeUp>
          <p className="flex items-center gap-3 text-cyan text-xs uppercase tracking-[0.22em] mb-5">
            <span className="h-px w-10 bg-cyan inline-block" />
            Navegue
          </p>
          <h2 className="font-display font-semibold tracking-tight text-4xl md:text-6xl leading-none">
            Três mundos,
            <br />
            uma loja.
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 auto-rows-[230px] mt-12">
          {categories.map((c, i) => (
            <FadeUp key={c.id} delay={i * 0.08} className={c.span}>
              <Link
                to={`/loja?categoria=${c.id}`}
                className={`group relative block h-full min-h-[230px] overflow-hidden rounded-lg border border-white/10 hover:border-cyan/40 transition-colors duration-300 ${c.span}`}
                data-testid={`category-card-${c.id}`}
              >
                <img
                  src={c.img}
                  alt={c.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 flex items-end justify-between gap-4">
                  <div>
                    <h3 className="font-display font-semibold text-3xl md:text-4xl">
                      {c.title}
                    </h3>
                    <p className="text-mute text-sm mt-1.5 max-w-xs">{c.copy}</p>
                  </div>
                  <span className="h-11 w-11 shrink-0 rounded-full border border-white/20 flex items-center justify-center text-white transition-all duration-300 group-hover:bg-cyan group-hover:text-ink group-hover:border-cyan group-hover:rotate-45">
                    <ArrowUpRight size={18} />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* DESTAQUES */}
      <section id="destaques" className="mx-auto max-w-7xl px-5 lg:px-10 py-14 md:py-20 scroll-mt-20" data-testid="home-featured">
        <FadeUp className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="flex items-center gap-3 text-cyan text-xs uppercase tracking-[0.22em] mb-5">
              <span className="h-px w-10 bg-cyan inline-block" />
              Selecionados para você
            </p>
            <h2 className="font-display font-semibold tracking-tight text-4xl md:text-6xl leading-none">
              Destaques da casa.
            </h2>
          </div>
          <Link
            to="/loja"
            className="group inline-flex items-center gap-2 text-sm text-mute hover:text-cyan transition-colors pb-1 border-b border-white/15 hover:border-cyan"
          >
            Ver tudo
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </FadeUp>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
          {featured.length === 0 && (
            <p className="text-mute col-span-full">
              O catálogo está sendo recarregado...
            </p>
          )}
        </div>
      </section>

      {/* TELAS / REPARO */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20 md:py-28" data-testid="home-telas">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <FadeUp>
            <p className="flex items-center gap-3 text-cyan text-xs uppercase tracking-[0.22em] mb-5">
              <span className="h-px w-10 bg-cyan inline-block" />
              Reparo & reposição
            </p>
            <h2 className="font-display font-semibold tracking-tight text-4xl md:text-6xl leading-[0.95]">
              Tela quebrada?
              <br />
              <span className="text-cyan">A gente resolve.</span>
            </h2>
            <p className="text-mute leading-relaxed mt-6 max-w-md">
              Trabalhamos com painéis de reposição de alta fidelidade para as
              principais linhas do mercado. Cada tela passa por teste de toque,
              cor e brilho antes de ir para o catálogo.
            </p>
            <ul className="mt-7 space-y-3.5">
              {[
                "Painéis testados peça por peça",
                "Kits com ferramentas inclusas",
                "Enviamos para todo o Brasil",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm">
                  <span className="h-5 w-5 rounded-full bg-cyan/10 border border-cyan/40 flex items-center justify-center">
                    <Check size={12} className="text-cyan" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/loja?categoria=telas"
              className="mt-9 inline-flex h-12 items-center gap-2 rounded-md border border-cyan/50 px-7 text-cyan font-medium transition-all duration-300 hover:bg-cyan hover:text-ink hover:-translate-y-0.5"
              data-testid="telas-cta"
            >
              Ver telas disponíveis
              <ArrowRight size={18} />
            </Link>
          </FadeUp>
          <FadeUp delay={0.1} className="relative">
            <div className="relative h-[420px] md:h-[520px]">
              <div className="absolute inset-0 rounded-lg overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1550041473-d296a3a8a18a?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Técnico reparando a tela de um celular"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-cyan/15 via-transparent to-transparent" />
              </div>
              <img
                src="https://images.pexels.com/photos/11921157/pexels-photo-11921157.jpeg?auto=compress&cs=tinysrgb&w=640"
                alt="Telas de celular de reposição"
                loading="lazy"
                className="absolute -bottom-8 -left-4 md:-left-8 w-44 md:w-56 rounded-md border-4 border-ink shadow-2xl shadow-cyan/10"
              />
              <div className="absolute -top-3 -right-3 h-full w-full border border-cyan/25 rounded-lg -z-10" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 lg:px-10 pb-8">
        <FadeUp>
          <div className="relative overflow-hidden rounded-lg border border-white/10 bg-elevated p-10 md:p-20">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(520px 300px at 85% 0%, rgba(0,229,255,0.16), transparent 70%)",
              }}
            />
            <div className="relative max-w-2xl">
              <h2 className="font-display font-semibold tracking-tight text-4xl md:text-6xl leading-[0.95]">
                Pronto para dar
                <br />
                um upgrade?
              </h2>
              <p className="text-mute mt-5 max-w-md">
                Celulares, acessórios e telas com pagamento seguro no cartão.
                Sem cadastro, sem complicação.
              </p>
              <Link
                to="/loja"
                className="mt-9 inline-flex h-12 items-center gap-2 rounded-md bg-cyan px-8 font-semibold text-ink transition-all duration-300 hover:bg-cyan-dark hover:-translate-y-0.5"
                data-testid="home-cta-banner"
              >
                Começar agora
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
